import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { extractErr } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Wallet, Copy, Check, Share2, Gift, Landmark, Users,
  Sparkles, ArrowUpRight, Loader2, IndianRupee, ShieldCheck, Infinity,
} from "lucide-react";
import { useTranslation } from "react-i18next";

const INR = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmt = (v) => {
  if (!v) return "—";
  try { return new Date(v).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return "—"; }
};

const WITHDRAW_MSG = "Balance will be transferred to you mentioned bank account or upi id with in 24-48hrs";

export default function MerchantWallet() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [payout, setPayout] = useState({ account_holder: "", account_number: "", ifsc: "", bank_name: "", upi_id: "" });
  const [savingPay, setSavingPay] = useState(false);
  const [wdAmt, setWdAmt] = useState("");
  const [wdBusy, setWdBusy] = useState(false);
  const [wdNotice, setWdNotice] = useState("");

  const load = async () => {
    try {
      const { data: d } = await api.get("/merchant/wallet");
      setData(d);
      setPayout({
        account_holder: d.payout?.account_holder || "",
        account_number: d.payout?.account_number || "",
        ifsc: d.payout?.ifsc || "",
        bank_name: d.payout?.bank_name || "",
        upi_id: d.payout?.upi_id || "",
      });
      window.dispatchEvent(new CustomEvent("wallet-updated", { detail: d.balance }));
    } catch (e) {
      toast.error(extractErr(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(data.referral_url);
      setCopied(true);
      toast.success("Referral link copied");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Could not copy");
    }
  };

  const waShare = () => {
    const text = encodeURIComponent(
      `Join me on MeraStartup — build your online store. Use my link and get started: ${data.referral_url}`
    );
    window.open(`https://wa.me/?text=${text}`, "_blank", "noopener,noreferrer");
  };

  const savePayout = async (e) => {
    e.preventDefault();
    setSavingPay(true);
    try {
      await api.put("/merchant/wallet/payout", payout);
      toast.success("Payout details saved");
      load();
    } catch (err) {
      toast.error(extractErr(err));
    } finally {
      setSavingPay(false);
    }
  };

  const withdraw = async () => {
    const amount = Number(wdAmt || data?.balance || 0);
    if (!amount) return;
    setWdBusy(true);
    setWdNotice("");
    try {
      const { data: res } = await api.post("/merchant/wallet/withdraw", { amount });
      setWdNotice(res.message || WITHDRAW_MSG);
      toast.success("Withdrawal requested");
      setWdAmt("");
      load();
    } catch (err) {
      toast.error(extractErr(err));
    } finally {
      setWdBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-slate-500 text-sm py-16 justify-center">
        <Loader2 className="w-4 h-4 animate-spin" /> {t("common.loading")}
      </div>
    );
  }

  if (!data) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center max-w-lg mx-auto mt-8" data-testid="refer-wallet-error">
        <Wallet className="w-10 h-10 text-slate-300 mx-auto" />
        <h1 className="mt-3 font-display text-xl font-bold text-slate-900">Couldn’t load wallet</h1>
        <p className="text-sm text-slate-500 mt-1">Please try again. If this keeps happening, sign out and sign back in.</p>
        <Button className="mt-4" onClick={() => { setLoading(true); load(); }}>Retry</Button>
      </div>
    );
  }

  const pct = data?.commission_percent ?? 10;
  const link = data?.referral_url || "";

  return (
    <div className="space-y-8 max-w-5xl" data-testid="refer-wallet-page">
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-emerald-700 via-teal-700 to-slate-900 text-white p-6 sm:p-8 shadow-xl">
        <div className="absolute -right-10 -top-10 w-56 h-56 rounded-full bg-amber-400/20 blur-3xl" />
        <div className="absolute -left-8 bottom-0 w-40 h-40 rounded-full bg-emerald-300/20 blur-2xl" />
        <div className="relative">
          <div className="inline-flex items-center gap-2 text-emerald-100 text-xs font-semibold uppercase tracking-wider bg-white/10 px-3 py-1 rounded-full border border-white/10">
            <Gift className="w-3.5 h-3.5" /> Refer & Earn
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold mt-3 tracking-tight">Earn {pct}% for life</h1>
          <p className="mt-2 text-emerald-50/90 max-w-xl text-sm sm:text-base leading-relaxed">
            Share your unique link. When a new merchant joins and pays, you earn {pct}% of every plan, app package, addon and renewal — forever. Direct referrals only. GST is never included.
          </p>
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div className="rounded-2xl bg-white/10 border border-white/10 p-4 backdrop-blur-sm">
              <div className="text-[11px] uppercase tracking-wider text-emerald-100/80">Wallet</div>
              <div className="text-2xl font-bold font-mono-nums mt-1" data-testid="wallet-balance-hero">{INR(data?.balance)}</div>
            </div>
            <div className="rounded-2xl bg-white/10 border border-white/10 p-4">
              <div className="text-[11px] uppercase tracking-wider text-emerald-100/80">Total earned</div>
              <div className="text-2xl font-bold font-mono-nums mt-1">{INR(data.total_earned)}</div>
            </div>
            <div className="rounded-2xl bg-white/10 border border-white/10 p-4 col-span-2 sm:col-span-1">
              <div className="text-[11px] uppercase tracking-wider text-emerald-100/80">Merchants referred</div>
              <div className="text-2xl font-bold font-mono-nums mt-1">{data.referred_count || 0}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-3xl border border-slate-200 bg-white p-5 sm:p-6 shadow-sm">
        <div className="flex items-center gap-2 text-slate-800 font-semibold">
          <Share2 className="w-4 h-4 text-emerald-600" /> Your unique referral link
        </div>
        <p className="text-sm text-slate-500 mt-1">Copy and share on WhatsApp, Instagram or anywhere you like.</p>
        <div className="mt-4 flex flex-col sm:flex-row gap-2">
          <div className="flex-1 font-mono text-sm bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 truncate" data-testid="referral-link">{link}</div>
          <Button onClick={copy} data-testid="copy-referral-link" className="bg-slate-900 hover:bg-slate-800">
            {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
            {copied ? "Copied" : "Copy link"}
          </Button>
          <Button variant="outline" onClick={waShare} data-testid="share-whatsapp">
            Share on WhatsApp
          </Button>
        </div>
        <div className="mt-3 text-xs text-slate-400">Code: <span className="font-mono text-slate-600">{data.referral_code}</span></div>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        {[
          { icon: Share2, title: "Share your link", body: "Send it to other shop owners who need a website or app." },
          { icon: Sparkles, title: "They join & pay", body: `You earn ${pct}% of the plan list price — even if they use a 50%, 60% or 100% coupon.` },
          { icon: Infinity, title: "Lifetime, every purchase", body: "Plans, addons, app package and renewals. GST is excluded (it goes to the government)." },
        ].map((s) => (
          <div key={s.title} className="rounded-2xl border border-slate-200 bg-white p-5">
            <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center">
              <s.icon className="w-5 h-5" />
            </div>
            <div className="mt-3 font-semibold text-slate-900">{s.title}</div>
            <p className="text-sm text-slate-500 mt-1 leading-relaxed">{s.body}</p>
          </div>
        ))}
      </div>
      <p className="text-xs text-slate-400 -mt-2">
        Direct referrals only. If someone you referred brings another merchant, that earning is theirs — not yours.
      </p>

      <div className="grid lg:grid-cols-2 gap-6">
        <form onSubmit={savePayout} className="rounded-3xl border border-slate-200 bg-white p-5 sm:p-6 shadow-sm space-y-4">
          <div className="flex items-center gap-2 font-semibold text-slate-800">
            <Landmark className="w-4 h-4 text-slate-600" /> Bank & UPI for withdrawal
          </div>
          <p className="text-sm text-slate-500">Saved on your wallet. Used when you request a payout.</p>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <Label>UPI ID</Label>
              <Input value={payout.upi_id} onChange={(e) => setPayout({ ...payout, upi_id: e.target.value })} placeholder="name@upi" data-testid="wallet-upi" />
            </div>
            <div className="sm:col-span-2 text-[11px] uppercase tracking-wider text-slate-400 text-center">or bank account</div>
            <div className="sm:col-span-2">
              <Label>Account holder</Label>
              <Input value={payout.account_holder} onChange={(e) => setPayout({ ...payout, account_holder: e.target.value })} data-testid="wallet-holder" />
            </div>
            <div>
              <Label>Account number</Label>
              <Input value={payout.account_number} onChange={(e) => setPayout({ ...payout, account_number: e.target.value })} data-testid="wallet-acc" />
            </div>
            <div>
              <Label>IFSC</Label>
              <Input value={payout.ifsc} onChange={(e) => setPayout({ ...payout, ifsc: e.target.value })} data-testid="wallet-ifsc" />
            </div>
            <div className="sm:col-span-2">
              <Label>Bank name</Label>
              <Input value={payout.bank_name} onChange={(e) => setPayout({ ...payout, bank_name: e.target.value })} />
            </div>
          </div>
          <Button type="submit" disabled={savingPay} data-testid="save-payout">{savingPay ? "Saving…" : "Save payout details"}</Button>
        </form>

        <div className="rounded-3xl border border-amber-200 bg-gradient-to-b from-amber-50 to-white p-5 sm:p-6 shadow-sm space-y-4">
          <div className="flex items-center gap-2 font-semibold text-slate-800">
            <Wallet className="w-4 h-4 text-amber-600" /> Withdraw
          </div>
          <div className="text-3xl font-extrabold font-mono-nums text-slate-900">{INR(data.balance)}</div>
          <p className="text-sm text-slate-500">Available now. You can request a transfer anytime.</p>
          <div>
            <Label>Amount (₹)</Label>
            <Input type="number" min="1" step="0.01" value={wdAmt} onChange={(e) => setWdAmt(e.target.value)} placeholder={String(data.balance || "")} data-testid="withdraw-amount" />
          </div>
          <Button
            className="w-full bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold"
            disabled={wdBusy || !(data.balance > 0)}
            onClick={withdraw}
            data-testid="withdraw-submit"
          >
            {wdBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <IndianRupee className="w-4 h-4 mr-2" />}
            Request withdrawal
          </Button>
          {wdNotice && (
            <div className="rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm p-3" data-testid="withdraw-notice">
              <ShieldCheck className="w-4 h-4 inline mr-1" /> {wdNotice}
            </div>
          )}
          {!data.payout_ready && (
            <p className="text-xs text-amber-700">Save a UPI ID or bank account first.</p>
          )}
        </div>
      </div>

      <div className="rounded-3xl border border-slate-200 bg-white overflow-hidden shadow-sm">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2 font-semibold">
          <Users className="w-4 h-4 text-slate-500" /> Merchants you brought
        </div>
        {(data.referred_merchants || []).length === 0 ? (
          <p className="p-8 text-sm text-slate-500 text-center">No referrals yet. Share your link to start earning.</p>
        ) : (
          <div className="divide-y divide-slate-100">
            {data.referred_merchants.map((m) => (
              <div key={m.id} className="px-5 py-3 flex items-center justify-between gap-3">
                <div>
                  <div className="font-medium text-slate-800">{m.name || m.slug}</div>
                  <div className="text-xs text-slate-400 font-mono">/{m.slug}</div>
                </div>
                <div className="text-xs text-slate-500">{fmt(m.created_at)}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="rounded-3xl border border-slate-200 bg-white overflow-hidden shadow-sm">
        <div className="px-5 py-4 border-b border-slate-100 font-semibold">Wallet activity</div>
        {(data.ledger || []).length === 0 && (data.withdrawals || []).length === 0 ? (
          <p className="p-8 text-sm text-slate-500 text-center">Credits from referrals will appear here.</p>
        ) : (
          <div className="divide-y divide-slate-100">
            {(data.ledger || []).map((row) => (
              <div key={row.id} className="px-5 py-3 flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-800 truncate">{row.note || row.source}</div>
                  <div className="text-[11px] text-slate-400">{fmt(row.created_at)}{row.from_store_name ? ` · ${row.from_store_name}` : ""}</div>
                </div>
                <div className={`font-mono-nums font-semibold ${row.type === "credit" ? "text-emerald-600" : "text-slate-700"}`}>
                  {row.type === "credit" ? "+" : "−"}{INR(row.amount)}
                </div>
              </div>
            ))}
          </div>
        )}
        {(data.withdrawals || []).some((w) => w.status === "pending") && (
          <div className="px-5 py-3 bg-amber-50 text-amber-800 text-xs border-t border-amber-100">
            A withdrawal is pending. Our team transfers it within 24–48 hours.
          </div>
        )}
      </div>

      <Link to="/admin/billing" className="inline-flex items-center text-sm text-slate-500 hover:text-slate-800">
        View billing history <ArrowUpRight className="w-3.5 h-3.5 ml-1" />
      </Link>
    </div>
  );
}
